
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    Wrench, Scan, Cpu, ShieldCheck, ArrowRight,
    Smartphone, Tablet, Laptop, CheckCircle, Zap, FileText, Layout, Brain, History
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function AboutPage() {
    const navigate = useNavigate();

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.3
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: "spring", stiffness: 50 }
        }
    };

    return (
        <div className="min-h-screen bg-background grid-pattern text-foreground overflow-x-hidden selection:bg-primary/30">
            {/* Background Effects */}
            {/* grid-pattern removed from here as it is applied to the container */}
            <div className="fixed top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[128px] pointer-events-none z-0 mix-blend-screen animate-pulse-slow" />
            <div className="fixed bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[128px] pointer-events-none z-0 mix-blend-screen" />

            {/* Header */}
            <header className="fixed w-full top-0 z-50 border-b border-white/5 bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/60">
                <div className="w-full px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div
                        className="flex items-center gap-3 cursor-pointer group"
                        onClick={() => navigate('/')}
                    >
                        <div className="w-10 h-10 bg-primary flex items-center justify-center relative overflow-hidden rounded-sm group-hover:shadow-[0_0_20px_rgba(0,240,255,0.5)] transition-all duration-300">
                            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                            <Wrench className="w-5 h-5 text-white relative z-10" />
                        </div>
                        <div>
                            <h1 className="font-heading text-xl font-bold uppercase tracking-tight text-white group-hover:text-primary transition-colors">
                                FIXIT.AI
                            </h1>
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        className="text-xl text-white hover:text-primary font-heading font-bold uppercase tracking-tight"
                        onClick={() => navigate('/logs')}
                    >
                        <History className="w-5 h-5 sm:mr-2" />
                        <span className="hidden sm:inline">History</span>
                    </Button>
                </div>
            </header>

            <main className="relative z-10 pt-32 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                {/* Hero Section */}
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                    className="text-center mb-32"
                >
                    <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/80 text-xs font-mono mb-8 backdrop-blur-sm">
                        INSIDE FIXIT.AI
                        <span className="text-white/40">•</span>
                        V 1.0.0
                    </motion.div>

                    <motion.h1 variants={itemVariants} className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter text-white mb-6">
                        Repair with <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-blue-400 to-primary bg-300% animate-shine">
                            Superhuman Intelligence
                        </span>
                    </motion.h1>

                    <motion.p variants={itemVariants} className="text-lg md:text-xl text-text-muted max-w-2xl mx-auto leading-relaxed mb-10">
                        AI that understands devices, diagnoses failures, and guides repairs visually.
                    </motion.p>

                    <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Button
                            size="lg"
                            onClick={() => navigate('/')}
                            className="bg-primary text-black hover:bg-primary/90 text-lg h-14 px-8 rounded-full shadow-[0_0_40px_-10px_rgba(0,240,255,0.4)] hover:shadow-[0_0_60px_-10px_rgba(0,240,255,0.6)] transition-all duration-300"
                        >
                            Start Repairing Now
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                    </motion.div>
                </motion.div>

                {/* WHAT FIXIT.AI DOES */}
                <div className="mb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-10"
                    >
                        <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">What FixIt.AI Does</h2>
                        <p className="text-text-muted mb-4">Three capabilities powered by Gemini 3 that make repairs fast, visual, and reliable.</p>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-primary/10 to-blue-500/10 border border-primary/20 text-primary text-xs font-mono font-semibold tracking-wide">
                            <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                            BUILT ON GEMINI 3 AI
                        </div>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            {
                                icon: Scan,
                                title: "Understands the Device",
                                desc: "Uses camera input to recognize devices, components, and visible symptoms."
                            },
                            {
                                icon: Cpu,
                                title: "Reasons Like a Technician",
                                desc: "Reads full service manuals and correlates them with real-world signals to find root cause."
                            },
                            {
                                icon: Wrench,
                                title: "Shows, Not Just Tells",
                                desc: "Generates interactive step-by-step guidance and repair simulations for each fix."
                            }
                        ].map((feature, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.08 }}
                                className="group relative p-6 rounded-2xl bg-surface/50 border border-white/10 hover:border-primary/50 transition-colors"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center border border-white/10 group-hover:border-primary/40 transition-colors">
                                        <feature.icon className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-white font-bold text-base mb-1">{feature.title}</h3>
                                        <p className="text-text-muted text-sm leading-relaxed">{feature.desc}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* WHY IT'S DIFFERENT */}
                <div className="mb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="rounded-3xl bg-surface/50 border border-white/10 p-8 md:p-10 relative overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-blue-500/10 opacity-80" />
                        <div className="relative z-10 max-w-3xl">
                            <p className="font-heading text-sm uppercase tracking-wider text-primary mb-3">Why it’s different</p>
                            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                                Most tools search. FixIt.AI reasons.
                            </h2>
                            <p className="text-text-muted leading-relaxed">
                                FixIt.AI doesn’t rely on keywords or static instructions. It understands what’s happening, why it’s happening,
                                and how to fix it safely — in real time.
                            </p>
                        </div>
                    </motion.div>
                </div>

                {/* POWERED BY GEMINI 3 */}
                {/* POWERED BY GEMINI 3 */}
                <div className="mb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="rounded-3xl bg-surface/30 border border-white/10 p-8 md:p-10 relative overflow-hidden"
                    >
                        {/* Background glow */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-[80px] pointer-events-none" />

                        <div className="flex items-center gap-3 mb-8">
                            <Brain className="w-8 h-8 text-primary" />
                            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">
                                Powered by Gemini 3
                            </h2>
                        </div>

                        <p className="text-text-muted mb-8 max-w-2xl leading-relaxed">
                            FixIt.AI is built natively on Gemini 3 to enable capabilities that static AI tools cannot match.
                            We leverage the latest in multimodal reasoning to understand your world.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                {
                                    icon: Scan,
                                    label: "Multimodal Perception",
                                    desc: "Understands video, audio, and images simultaneously to perceive the problem."
                                },
                                {
                                    icon: FileText,
                                    label: "Long-Context Reasoning",
                                    desc: "Ingests and correlates entire technical manuals with observed symptoms."
                                },
                                {
                                    icon: Zap,
                                    label: "Real-Time Inference",
                                    desc: "Low-latency processing ensures guidance keeps up with your movements."
                                },
                                {
                                    icon: Layout,
                                    label: "Dynamic UI Generation",
                                    desc: "Instantly generates custom visual aids and interfaces for your specific repair."
                                }
                            ].map((item, i) => (
                                <motion.div
                                    key={i}
                                    whileHover={{ scale: 1.02, backgroundColor: "rgba(255, 255, 255, 0.05)" }}
                                    className="flex items-start gap-4 p-4 rounded-xl bg-surface/40 border border-white/5 transition-colors"
                                >
                                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <item.icon className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-white text-sm mb-1">{item.label}</h3>
                                        <p className="text-text-muted text-xs leading-relaxed">{item.desc}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* SAFETY FIRST */}
                <div className="mb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="rounded-3xl bg-gradient-to-br from-warning/5 to-transparent border border-warning/20 p-8 md:p-10 relative overflow-hidden"
                    >
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-warning to-transparent opacity-50" />

                        <div className="flex flex-col md:flex-row gap-8 items-start">
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center border border-warning/20">
                                        <ShieldCheck className="w-6 h-6 text-warning" />
                                    </div>
                                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">
                                        Safety FirstGuard™
                                    </h2>
                                </div>
                                <p className="text-text-muted mb-6 leading-relaxed">
                                    We prioritize your safety above all else. Before any repair step is shown,
                                    our rigorous safety protocols evaluate risk factors in real-time.
                                </p>
                                <div className="p-4 rounded-xl bg-warning/5 border border-warning/10">
                                    <p className="text-warning text-sm font-bold mb-1">AUTOMATED INTERVENTION</p>
                                    <p className="text-text-muted text-xs">
                                        If a repair is deemed unsafe for DIY, we immediately block the process and enable diagnosis-only mode with professional recommendations.
                                    </p>
                                </div>
                            </div>

                            <div className="w-full md:w-1/2 bg-black/40 rounded-2xl p-6 border border-white/5">
                                <h3 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Active Safety Protocols</h3>
                                <div className="space-y-3">
                                    {[
                                        "Real-time Risk Evaluation",
                                        "Required Tools Verification",
                                        "High-Voltage Component Detection",
                                        "Structural Integrity Analysis"
                                    ].map((item, i) => (
                                        <motion.div
                                            key={i}
                                            initial={{ x: -10, opacity: 0 }}
                                            whileInView={{ x: 0, opacity: 1 }}
                                            transition={{ delay: i * 0.1 }}
                                            className="flex items-center gap-3"
                                        >
                                            <CheckCircle className="w-5 h-5 text-success" />
                                            <span className="text-white/90 text-sm">{item}</span>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* ADAPTIVE BY DESIGN */}
                {/* ADAPTIVE BY DESIGN */}
                <div className="mb-24">
                    <div className="text-center mb-10">
                        <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">Built for Every Screen</h2>
                        <p className="text-text-muted">Same intelligence. Optimized experience for whatever device you have.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            {
                                icon: Smartphone,
                                label: "Mobile",
                                title: "Guided Focus",
                                desc: "One-step-at-a-time guidance ideal for working in tight spaces.",
                                color: "text-blue-400"
                            },
                            {
                                icon: Tablet,
                                label: "Tablet",
                                title: "Split-View",
                                desc: "Simultaneous visual guides and instructions for hands-free reference.",
                                color: "text-purple-400"
                            },
                            {
                                icon: Laptop,
                                label: "Desktop",
                                title: "Command Center",
                                desc: "Full diagnostic dashboard with deep-dive technical learning mode.",
                                color: "text-pink-400"
                            }
                        ].map((item, i) => (
                            <motion.div
                                key={item.label}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                whileHover={{ y: -5 }}
                                className="group p-6 rounded-2xl bg-surface/30 border border-white/10 hover:border-white/20 transition-all"
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <div className={`w-12 h-12 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors ${item.color}`}>
                                        <item.icon className="w-6 h-6" />
                                    </div>
                                    <span className="font-mono text-xs uppercase tracking-wider text-white/40">{item.label}</span>
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-primary transition-colors">{item.title}</h3>
                                <p className="text-text-muted text-sm leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* IMPACT STRIP */}
                <div className="mb-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="rounded-3xl bg-gradient-to-r from-primary/15 via-blue-500/10 to-primary/15 border border-white/10 p-8 md:p-10 text-center"
                    >
                        <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                            Repair Over Replace
                        </h2>
                        <p className="text-text-muted max-w-3xl mx-auto">
                            FixIt.AI helps extend device lifespan, reduce e-waste, and make professional repair knowledge accessible to everyone.
                        </p>
                    </motion.div>
                </div>

            </main>

            <footer className="border-t border-white/10 bg-background/80 backdrop-blur-sm relative z-10">
                <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <span className="font-mono text-xs text-text-muted">
                            FIXIT.AI © 2025 — AI-Powered Repair Assistant
                        </span>
                        <div className="flex items-center gap-4 text-text-muted text-xs">
                            <span>Powered by Gemini 3</span>
                            <span>•</span>
                            <span>Safety First</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
